/**
 * Layer Renderer
 * Main component that dispatches to type-specific renderers
 */

import React, { useCallback, useMemo } from 'react';
import type { Layer } from '@/types/layers';
import type { AdFormat } from '@/types/formats';
import { resolvePosition, getEffectiveLayerProps, type ContainerDimensions } from '@/lib/position/resolver';

// Import type-specific renderers
import { TextLayerRenderer } from './renderers/TextLayerRenderer';
import { PrimitiveLayerRenderer } from './renderers/PrimitiveLayerRenderer';
import { ShapeLayerRenderer } from './renderers/ShapeLayerRenderer';
import { SvgPathLayerRenderer } from './renderers/SvgPathLayerRenderer';
import { LottieLayerRenderer } from './renderers/LottieLayerRenderer';
import { RiveLayerRenderer } from './renderers/RiveLayerRenderer';
import { ImageLayerRenderer } from './renderers/ImageLayerRenderer';
import { VideoLayerRenderer } from './renderers/VideoLayerRenderer';
import { GroupLayerRenderer } from './renderers/GroupLayerRenderer';

interface LayerRendererProps {
  layers: Layer[];
  format: AdFormat;
  variableValues: Record<string, string>;
  selectedLayerIds: string[];
  hoveredLayerId: string | null;
  onLayerClick?: (layerId: string, event: React.MouseEvent) => void;
  onLayerHover?: (layerId: string | null) => void;
  onLayerMouseDown?: (event: React.MouseEvent, layerId: string) => void;
}

export function LayerRenderer({
  layers,
  format,
  variableValues,
  selectedLayerIds,
  hoveredLayerId,
  onLayerClick,
  onLayerHover,
  onLayerMouseDown,
}: LayerRendererProps) {
  const containerDimensions: ContainerDimensions = useMemo(
    () => ({ width: format.width, height: format.height }),
    [format.width, format.height]
  );

  // Get default layer dimensions based on type
  const getLayerDimensions = useCallback(
    (layer: Layer): { width: number; height: number } => {
      const effectiveLayer = getEffectiveLayerProps(layer, format);

      // Handle explicit dimensions
      if (typeof effectiveLayer.width === 'number' && typeof effectiveLayer.height === 'number') {
        return { width: effectiveLayer.width, height: effectiveLayer.height };
      }

      // Type-specific defaults
      switch (layer.type) {
        case 'text':
          return { width: 300, height: 50 };
        case 'primitive':
          return { width: layer.content.size ?? 200, height: layer.content.size ?? 200 };
        case 'shape':
          return { width: 100, height: 100 };
        case 'image':
          return { width: 200, height: 150 };
        case 'video':
          return { width: 320, height: 180 };
        case 'group':
          return { width: containerDimensions.width, height: containerDimensions.height };
        default:
          return { width: 100, height: 100 };
      }
    },
    [format, containerDimensions]
  );

  // Render a single layer
  const renderLayer = useCallback(
    (layer: Layer): React.ReactNode => {
      if (!layer.visible) return null;

      const effectiveLayer = getEffectiveLayerProps(layer, format);
      const layerDims = getLayerDimensions(effectiveLayer);
      const position = resolvePosition(effectiveLayer.position, containerDimensions, layerDims);

      const isSelected = selectedLayerIds.includes(layer.id);
      const isHovered = hoveredLayerId === layer.id;

      const handleClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        onLayerClick?.(layer.id, e);
      };

      const handleMouseDown = (e: React.MouseEvent) => {
        e.stopPropagation();
        onLayerMouseDown?.(e, layer.id);
      };

      const handleMouseEnter = () => {
        onLayerHover?.(layer.id);
      };

      const handleMouseLeave = () => {
        onLayerHover?.(null);
      };

      const commonProps = {
        isSelected,
        isHovered,
        onClick: handleClick,
        onMouseDown: handleMouseDown,
        onMouseEnter: handleMouseEnter,
        onMouseLeave: handleMouseLeave,
      };

      switch (effectiveLayer.type) {
        case 'text':
          return (
            <TextLayerRenderer
              key={layer.id}
              layer={effectiveLayer}
              position={position}
              variableValues={variableValues}
              {...commonProps}
            />
          );

        case 'primitive':
          return (
            <PrimitiveLayerRenderer
              key={layer.id}
              layer={effectiveLayer}
              position={position}
              variableValues={variableValues}
              {...commonProps}
            />
          );

        case 'shape':
          return (
            <ShapeLayerRenderer
              key={layer.id}
              layer={effectiveLayer}
              position={position}
              {...commonProps}
            />
          );

        case 'svg-path':
          return (
            <SvgPathLayerRenderer
              key={layer.id}
              layer={effectiveLayer}
              position={position}
              {...commonProps}
            />
          );

        case 'lottie':
          return (
            <LottieLayerRenderer
              key={layer.id}
              layer={effectiveLayer}
              position={position}
              {...commonProps}
            />
          );

        case 'rive':
          return (
            <RiveLayerRenderer
              key={layer.id}
              layer={effectiveLayer}
              position={position}
              {...commonProps}
            />
          );

        case 'image':
          return (
            <ImageLayerRenderer
              key={layer.id}
              layer={effectiveLayer}
              position={position}
              {...commonProps}
            />
          );

        case 'video':
          return (
            <VideoLayerRenderer
              key={layer.id}
              layer={effectiveLayer}
              position={position}
              {...commonProps}
            />
          );

        case 'group':
          return (
            <GroupLayerRenderer
              key={layer.id}
              layer={effectiveLayer}
              position={position}
              containerDimensions={containerDimensions}
              variableValues={variableValues}
              selectedLayerIds={selectedLayerIds}
              hoveredLayerId={hoveredLayerId}
              renderLayer={renderLayer}
              {...commonProps}
            />
          );

        default:
          return null;
      }
    },
    [
      format,
      containerDimensions,
      variableValues,
      selectedLayerIds,
      hoveredLayerId,
      onLayerClick,
      onLayerHover,
      onLayerMouseDown,
      getLayerDimensions,
    ]
  );

  return <>{layers.filter((layer) => layer.visible).map(renderLayer)}</>;
}

// Export renderer components for direct use
export { TextLayerRenderer } from './renderers/TextLayerRenderer';
export { PrimitiveLayerRenderer } from './renderers/PrimitiveLayerRenderer';
export { ShapeLayerRenderer } from './renderers/ShapeLayerRenderer';
export { SvgPathLayerRenderer } from './renderers/SvgPathLayerRenderer';
export { LottieLayerRenderer } from './renderers/LottieLayerRenderer';
export { RiveLayerRenderer } from './renderers/RiveLayerRenderer';
export { ImageLayerRenderer } from './renderers/ImageLayerRenderer';
export { VideoLayerRenderer } from './renderers/VideoLayerRenderer';
export { GroupLayerRenderer } from './renderers/GroupLayerRenderer';
